import { useState, useEffect } from "react";
import { BarChart3, TrendingUp, CalendarDays, Users, FileDown, Download } from "lucide-react";
import { format, subDays, startOfMonth } from "date-fns";
import { toast } from "sonner";
import api, { API, errorMessage } from "@/lib/api";

const formatBRL = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0, maximumFractionDigits: 0 });

const PERIODOS = [
  { id: "hoje", label: "Hoje" },
  { id: "7d", label: "7 dias" },
  { id: "mes", label: "Este mês" },
  { id: "30d", label: "30 dias" },
  { id: "90d", label: "90 dias" },
  { id: "all", label: "Todos" },
  { id: "custom", label: "Personalizado" },
];

const ymd = (d) => format(d, "yyyy-MM-dd");

const periodRange = (periodo, custom) => {
  const hoje = new Date();
  switch (periodo) {
    case "hoje": return [ymd(hoje), ymd(hoje)];
    case "7d": return [ymd(subDays(hoje, 6)), ymd(hoje)];
    case "mes": return [ymd(startOfMonth(hoje)), ymd(hoje)];
    case "30d": return [ymd(subDays(hoje, 29)), ymd(hoje)];
    case "90d": return [ymd(subDays(hoje, 89)), ymd(hoje)];
    case "custom": return [custom.inicio || "", custom.fim || ""];
    default: return ["", ""];
  }
};

const buildPeriodParams = (periodo, custom) => {
  const [inicio, fim] = periodRange(periodo, custom);
  const qs = new URLSearchParams();
  if (inicio) qs.set("data_inicio", inicio);
  if (fim) qs.set("data_fim", fim);
  const s = qs.toString();
  return s ? `?${s}` : "";
};

export function Relatorios() {
  const [data, setData] = useState({ campanhas: [], totais: {} });
  const [periodo, setPeriodo] = useState("all");
  const [exportingId, setExportingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [custom, setCustom] = useState({ inicio: ymd(subDays(new Date(), 30)), fim: ymd(new Date()) });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { loadReport(); }, [periodo, custom.inicio, custom.fim]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadReport = async () => {
    if (periodo === "custom" && custom.inicio && custom.fim && custom.inicio > custom.fim) {
      setError("A data inicial é maior que a final."); return;
    }
    setLoading(true); setError("");
    try {
      const res = await api.get(`/reports${buildPeriodParams(periodo, custom)}`);
      setData({ campanhas: res.data?.campanhas || [], totais: res.data?.totais || {}, ultimo: res.data?.ultimo_lead_em || null });
    } catch (e) {
      setData({ campanhas: [], totais: {} });
      setError(errorMessage(e, "Não foi possível carregar o relatório."));
    } finally { setLoading(false); }
  };

  const handleExport = async (campanha, formato) => {
    if (!campanha.pipeline_id) return;
    const key = `${campanha.pipeline_id}-${formato}`;
    setExportingId(key);
    try {
      const url = `${API}/reports/${campanha.pipeline_id}/export.${formato}${buildPeriodParams(periodo, custom)}`;
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: formato === "pdf" ? "application/pdf" : "text/csv;charset=utf-8" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const cd = res.headers["content-disposition"] || "";
      let filename = `relatorio_${campanha.campanha}.${formato}`;
      const match = cd.match(/filename\*=UTF-8''([^;]+)/i) || cd.match(/filename="?([^";]+)"?/i);
      if (match && match[1]) filename = decodeURIComponent(match[1]);
      link.download = filename;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      toast.success(`Exportado (${formato.toUpperCase()})`);
    } catch (e) {
      toast.error(errorMessage(e, "Erro ao exportar"));
    } finally { setExportingId(null); }
  };

  const t = data.totais || {};

  return (
    <div className="h-full overflow-y-auto px-6 py-6" data-testid="relatorios-view">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-zinc-50">Relatórios</h2>
          <p className="text-xs text-zinc-500 mt-0.5">Visão geral das campanhas · período pela data de cadastro do lead</p>
        </div>
        <div className="flex bg-zinc-800 rounded-md p-0.5" data-testid="periodo-selector">
          {PERIODOS.map((p) => (
            <button key={p.id} data-testid={`periodo-${p.id}`} onClick={() => setPeriodo(p.id)} className={`px-3 py-1 text-xs rounded transition-all ${periodo === p.id ? "bg-zinc-700 text-zinc-100" : "text-zinc-500 hover:text-zinc-300"}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>
      {periodo === "custom" && (
        <div className="flex items-center gap-2 mb-6 -mt-3 justify-end" data-testid="periodo-custom">
          <span className="text-xs text-zinc-500">De</span>
          <input type="date" data-testid="periodo-custom-inicio" value={custom.inicio} onChange={(e) => setCustom((c) => ({ ...c, inicio: e.target.value }))} className="h-8 text-xs rounded-md border border-zinc-700 bg-zinc-900 px-2 text-zinc-200" />
          <span className="text-xs text-zinc-500">até</span>
          <input type="date" data-testid="periodo-custom-fim" value={custom.fim} onChange={(e) => setCustom((c) => ({ ...c, fim: e.target.value }))} className="h-8 text-xs rounded-md border border-zinc-700 bg-zinc-900 px-2 text-zinc-200" />
        </div>
      )}
      {error && (
        <div className="mb-6 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-xs text-red-700 flex items-center justify-between" data-testid="report-error">
          <span>{error}</span>
          <button onClick={loadReport} className="underline font-medium" data-testid="report-retry">Tentar de novo</button>
        </div>
      )}
      {loading && <p className="text-xs text-zinc-500 mb-4" data-testid="report-loading">Carregando relatório...</p>}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2"><Users className="h-4 w-4 text-zinc-500" /><span className="text-xs text-zinc-500">Total leads</span></div>
          <p className="text-2xl font-bold text-zinc-50 tabular-nums" data-testid="report-total-leads">{t.total_leads || 0}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2"><CalendarDays className="h-4 w-4 text-zinc-500" /><span className="text-xs text-zinc-500">Agendados</span></div>
          <p className="text-2xl font-bold text-zinc-50 tabular-nums" data-testid="report-total-agendados">{t.agendados || 0}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2"><TrendingUp className="h-4 w-4 text-zinc-500" /><span className="text-xs text-zinc-500">Conversão</span></div>
          <p className="text-2xl font-bold text-zinc-50 tabular-nums" data-testid="report-conversao">{t.conversao || 0}%</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2"><BarChart3 className="h-4 w-4 text-zinc-500" /><span className="text-xs text-zinc-500">Valor potencial</span></div>
          <p className="text-2xl font-bold text-zinc-50 tabular-nums" data-testid="report-valor">{formatBRL(t.valor_potencial)}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2"><CalendarDays className="h-4 w-4 text-green-500" /><span className="text-xs text-zinc-500">Realizado</span></div>
          <p className="text-2xl font-bold text-green-400 tabular-nums" data-testid="report-realizado">{formatBRL(t.valor_realizado)}</p>
        </div>
      </div>

      {data.campanhas.length === 0 && !loading ? (
        <div className="text-center py-20">
          <BarChart3 className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-sm text-zinc-600" data-testid="report-empty">Nenhum lead cadastrado neste período</p>
          {data.ultimo && (
            <p className="text-xs text-zinc-500 mt-1" data-testid="report-ultimo-lead">
              Último lead cadastrado em {data.ultimo.split("-").reverse().join("/")}.{" "}
              <button onClick={() => setPeriodo("all")} className="underline font-medium" data-testid="report-ver-todos">Ver todos</button>
            </p>
          )}
        </div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
          <table className="w-full" data-testid="report-table">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Campanha</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Leads</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Agendados</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Conversão</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Valor potencial</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Realizado</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Exportar</th>
              </tr>
            </thead>
            <tbody>
              {data.campanhas.map((c, i) => {
                const pdfKey = `${c.pipeline_id}-pdf`;
                const csvKey = `${c.pipeline_id}-csv`;
                const motivos = c.motivos_perdido || {};
                const expanded = expandedId === c.pipeline_id;
                const temMotivos = Object.keys(motivos).length > 0;
                const rowKey = c.pipeline_id || `row-${i}`;
                return [
                    <tr key={rowKey} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors">
                      <td className="px-4 py-3 text-sm text-zinc-200 font-medium">
                        {c.campanha}
                        {temMotivos && (
                          <button onClick={() => setExpandedId(expanded ? null : c.pipeline_id)} className="ml-2 text-[10px] text-amber-400 hover:text-amber-300 underline" data-testid={`motivos-toggle-${c.pipeline_id}`}>
                            {expanded ? "ocultar" : "motivos"}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-zinc-300 text-right tabular-nums">{c.total_leads}</td>
                      <td className="px-4 py-3 text-sm text-zinc-300 text-right tabular-nums">{c.agendados}</td>
                      <td className="px-4 py-3 text-right"><span className={`text-sm font-medium tabular-nums ${c.conversao >= 10 ? "text-green-400" : c.conversao >= 5 ? "text-amber-400" : "text-zinc-400"}`}>{c.conversao}%</span></td>
                      <td className="px-4 py-3 text-sm text-zinc-300 text-right tabular-nums">{formatBRL(c.valor_potencial)}</td>
                      <td className="px-4 py-3 text-sm text-green-400 text-right tabular-nums font-medium">{formatBRL(c.valor_realizado)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          <button data-testid={`export-pdf-${c.pipeline_id}`} onClick={() => handleExport(c, "pdf")} disabled={!c.pipeline_id || exportingId === pdfKey} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 disabled:opacity-50 transition-colors" title="Exportar PDF">
                            <FileDown className="h-3 w-3" />{exportingId === pdfKey ? "..." : "PDF"}
                          </button>
                          <button data-testid={`export-csv-${c.pipeline_id}`} onClick={() => handleExport(c, "csv")} disabled={!c.pipeline_id || exportingId === csvKey} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200 disabled:opacity-50 transition-colors" title="Exportar CSV">
                            <Download className="h-3 w-3" />{exportingId === csvKey ? "..." : "CSV"}
                          </button>
                        </div>
                      </td>
                    </tr>,
                    expanded && temMotivos ? (
                      <tr key={`${rowKey}-exp`} className="bg-zinc-800/20">
                        <td colSpan={7} className="px-4 py-3">
                          <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-2">Motivos de não converter</p>
                          <div className="flex flex-wrap gap-2" data-testid={`motivos-${c.pipeline_id}`}>
                            {Object.entries(motivos).sort((a, b) => b[1] - a[1]).map(([m, n]) => (
                              <span key={m} className="inline-flex items-center gap-1 text-xs bg-zinc-800 text-zinc-200 px-2 py-1 rounded">
                                {m} <span className="text-zinc-500">· {n}</span>
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ) : null,
                ];
              })}
            </tbody>
            <tfoot>
              <tr className="bg-zinc-800/50">
                <td className="px-4 py-3 text-sm text-zinc-100 font-semibold">Total</td>
                <td className="px-4 py-3 text-sm text-zinc-100 text-right font-semibold tabular-nums">{t.total_leads || 0}</td>
                <td className="px-4 py-3 text-sm text-zinc-100 text-right font-semibold tabular-nums">{t.agendados || 0}</td>
                <td className="px-4 py-3 text-sm text-zinc-100 text-right font-semibold tabular-nums">{t.conversao || 0}%</td>
                <td className="px-4 py-3 text-sm text-zinc-100 text-right font-semibold tabular-nums">{formatBRL(t.valor_potencial)}</td>
                <td className="px-4 py-3 text-sm text-green-400 text-right font-semibold tabular-nums">{formatBRL(t.valor_realizado)}</td>
                <td className="px-4 py-3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
